import React, { useState } from 'react';
import axios from 'axios';
import { Shield, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MilitaryContact = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    organization: '',
    email: '',
    phone: '',
    installation_name: '',
    installation_type: '',
    power_generation_capacity: '',
    heating_systems: '',
    permit_needs: '',
    timeline: '',
    specific_requirements: '',
    additional_info: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        subject: `Military Installation Inquiry - ${formData.installation_name}`,
        message: `
Organization: ${formData.organization}
Phone: ${formData.phone}
Installation Name: ${formData.installation_name}
Installation Type: ${formData.installation_type}
Power Generation Capacity: ${formData.power_generation_capacity}
Heating Systems: ${formData.heating_systems}
Permit Needs: ${formData.permit_needs}
Timeline: ${formData.timeline}
Specific Requirements or Concerns: ${formData.specific_requirements}
Additional Information: ${formData.additional_info}
        `.trim(),
        newsletter_signup: false,
      };

      const response = await axios.post(`${API}/contact`, payload);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your military installation requirements and contact you within 1-2 business days.',
        });
        setFormData({
          first_name: '',
          last_name: '',
          organization: '',
          email: '',
          phone: '',
          installation_name: '',
          installation_type: '',
          power_generation_capacity: '',
          heating_systems: '',
          permit_needs: '',
          timeline: '',
          specific_requirements: '',
          additional_info: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-green-900 to-green-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Shield className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Military Installation Inquiry</h1>
                <p className="text-green-100 mt-2">Air quality and environmental compliance for military facilities</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Organization/Unit <span className="text-red-500">*</span></label>
                    <input type="text" name="organization" value={formData.organization} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Installation Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Installation Name</label>
                    <input type="text" name="installation_name" value={formData.installation_name} onChange={handleChange} placeholder="e.g., Fort Wainwright, Eielson AFB" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Installation Type</label>
                    <select name="installation_type" value={formData.installation_type} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                      <option value="">Select installation type</option>
                      <option value="army_base">Army Base</option>
                      <option value="air_force_base">Air Force Base</option>
                      <option value="coast_guard">Coast Guard Facility</option>
                      <option value="naval_facility">Naval Facility</option>
                      <option value="national_guard">National Guard</option>
                      <option value="training_facility">Training Facility</option>
                      <option value="other">Other Federal Facility</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Power Generation Capacity</label>
                    <input type="text" name="power_generation_capacity" value={formData.power_generation_capacity} onChange={handleChange} placeholder="e.g., 5 MW, 10 diesel generators" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heating Systems</label>
                    <select name="heating_systems" value={formData.heating_systems} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                      <option value="">Select heating system</option>
                      <option value="boilers">Boiler Systems</option>
                      <option value="district_heating">District Heating</option>
                      <option value="individual_units">Individual Heating Units</option>
                      <option value="combined">Combined Systems</option>
                      <option value="not_applicable">Not Applicable</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permit Needs</label>
                    <select name="permit_needs" value={formData.permit_needs} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                      <option value="">Select primary need</option>
                      <option value="new_permit">New Air Quality Permit</option>
                      <option value="title_v">Title V Operating Permit</option>
                      <option value="permit_renewal">Permit Renewal</option>
                      <option value="modification">Facility Modification</option>
                      <option value="compliance_support">Ongoing Compliance Support</option>
                      <option value="assessment">Compliance Assessment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                      <option value="">Select timeline</option>
                      <option value="immediate">Within 2 weeks</option>
                      <option value="1_month">Within 1 month</option>
                      <option value="1-3_months">1-3 months</option>
                      <option value="3-6_months">3-6 months</option>
                      <option value="6+_months">6+ months</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specific_requirements" value={formData.specific_requirements} onChange={handleChange} required rows="4" placeholder="Specific environmental concerns, operational constraints, or requirements..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additional_info" value={formData.additional_info} onChange={handleChange} rows="3" placeholder="Any other details..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-green-800 text-white py-4 px-6 rounded-lg font-semibold hover:bg-green-900 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Military Installation Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MilitaryContact;
